import type { ComputedRef, Ref } from "vue";
import { listingPagePath } from "#shared/utils/listingTypes";
import {
  RECHERCHE_THEMES,
  themeCanonique,
} from "~/composables/useRechercheHelpers";

// Type importé de la SOURCE UNIQUE (`#shared/utils/listingTypes`) plutôt que
// redéclaré : cinq copies de cette union vivaient ici, et l'ajout du rayon
// « boutiques » (2026-08-17) les aurait fait diverger une à une.
import type { ValidType } from "#shared/utils/listingTypes";
import { fuseauDe, isoAvecDecalage } from "~/utils/heureAssociation";
import { lieuSchemaDepuisMorceaux } from "#shared/utils/lieuPublic";

interface SeoEntry {
  title: string;
  description: string;
  canonical: string;
  keywords: string;
}

interface SeoSources {
  activeType: Ref<ValidType> | ComputedRef<ValidType>;
  categoryFromUrl: Ref<string> | ComputedRef<string>;
  /** Page courante (1-indexée) des routes `/xxx/page/N`. */
  currentPage?: Ref<number> | ComputedRef<number>;
  /** Nombre total de pages du rayon actif. */
  totalPages?: Ref<number> | ComputedRef<number>;
  campaignsTotal: Ref<number>;
  eventsTotal: Ref<number>;
  adhesionsTotal: Ref<number>;
  qrCodesTotal: Ref<number>;
  organizationsTotal: Ref<number>;
  shopsTotal: Ref<number>;
  campaigns: Ref<Array<{ title: string; slug: string; organization_name: string }>>;
  events: Ref<Array<{ title: string; slug: string; start_date?: string | null; location_name?: string | null }>>;
  adhesions: Ref<Array<{ title: string; slug: string; price: string }>>;
  organizations: Ref<Array<{ name: string; slug: string }>>;
  tabs: Array<{ key: string; label: string }>;
}

/**
 * useRechercheSeo — Centralise la config SEO complète de la page /recherche.
 *
 * Génère :
 *   - useSeoMeta (title, description, og:*, twitter, keywords, robots)
 *   - useHead (canonical link + BreadcrumbList + CollectionPage JSON-LD)
 *
 * Les textes varient selon le type actif et la catégorie courante.
 * Les compteurs (totaux) et les data refs sont injectés pour que les
 * descriptions contiennent les chiffres réels et que JSON-LD liste
 * jusqu'à 20 items concrets.
 *
 * Retourne `seoConfig` pour usage interne par la page (ex: titre H1).
 */
export function useRechercheSeo(sources: SeoSources): {
  seoConfig: ComputedRef<SeoEntry>;
} {
  const {
    activeType,
    categoryFromUrl,
    currentPage,
    totalPages,
    campaignsTotal,
    eventsTotal,
    adhesionsTotal,
    qrCodesTotal,
    organizationsTotal,
    shopsTotal,
    campaigns,
    events,
    adhesions,
    organizations,
    tabs,
  } = sources;

  // Origine du site COURANT (prod/staging/préview) pour l'og:image
  // auto-référente — cf. useShareBaseUrl.
  const shareBase = useShareBaseUrl();
  const { t } = useI18n();

  const seoConfig = computed<SeoEntry>(() => {
    // Libellé HUMAIN de la catégorie, jamais le slug brut de l'URL.
    // AVANT (bug relevé en prod le 2026-08-06) : `catLabel` valait le slug tel
    // quel — le titre indexé de /cagnottes?category=solidarite était
    // « Cagnottes  solidarite en cours » : slug sans accent, sans majuscule, et
    // DOUBLE espace (catLabel portait déjà son espace de tête, et chaque
    // gabarit en rajoutait un). On passe par le référentiel de libellés utilisé
    // par les filtres, et l'espace de tête est ajouté une seule fois ici.
    const rawCat = categoryFromUrl.value;
    const catLabel = (() => {
      if (!rawCat) return "";
      // Liste de thèmes COMMUNE à tous les rayons (2026-09-26) ; une ancienne
      // valeur d'un lien partagé (`?category=cultuelle`) est traduite.
      // Plusieurs thèmes (`?category=sport,loisirs`) : titre générique —
      // ces URLs à paramètres ne sont pas indexées (cf. plus bas).
      if (rawCat.includes(",")) return "";
      const normalized = themeCanonique(rawCat) ?? rawCat;
      const found = RECHERCHE_THEMES.find((c) => c.value === normalized);
      return ` ${found ? themeAffiche(found.value) : normalized}`;
    })();
    // Textes dans la langue de la page (domaine i18n `recherche`). Sans total
    // connu, phrase sans nombre plutôt qu'un « 0 cagnottes ».
    const cat = catLabel;
    const avecNombre = (cle: string, n: number, extra: Record<string, unknown> = {}) =>
      n ? t(`recherche.seo.${cle}.description`, { n, cat, ...extra }) : t(`recherche.seo.${cle}.descriptionSansNombre`, { cat, ...extra });
    const map: Record<ValidType, SeoEntry> = {
      tout: {
        title: t("recherche.seo.tout.title"),
        description:
          campaignsTotal.value && eventsTotal.value && adhesionsTotal.value
            ? t("recherche.seo.tout.description", {
                cagnottes: campaignsTotal.value,
                evenements: eventsTotal.value,
                adhesions: adhesionsTotal.value,
              })
            : t("recherche.seo.tout.descriptionSansNombre"),
        canonical: "https://donoor.org/recherche",
        keywords: t("recherche.seo.tout.keywords"),
      },
      boutiques: {
        title: t("recherche.seo.boutiques.title"),
        description: avecNombre("boutiques", shopsTotal.value),
        canonical: "https://donoor.org/recherche/boutiques",
        keywords: t("recherche.seo.boutiques.keywords"),
      },
      cagnottes: {
        // « cagnottes en cours » (63 imp, pos 7,5, 0 clic — audit GSC 2026-07-25) :
        // « en cours » ajouté au title pour matcher la requête réelle.
        title: t("recherche.seo.cagnottes.title", { cat }),
        description: avecNombre("cagnottes", campaignsTotal.value),
        canonical: "https://donoor.org/recherche/cagnottes",
        keywords: t("recherche.seo.cagnottes.keywords", { cat }),
      },
      evenements: {
        title: t("recherche.seo.evenements.title", { cat }),
        description: avecNombre("evenements", eventsTotal.value),
        canonical: "https://donoor.org/recherche/billetterie",
        keywords: t("recherche.seo.evenements.keywords", { cat }),
      },
      adhesions: {
        title: t("recherche.seo.adhesions.title", { cat }),
        description: avecNombre("adhesions", adhesionsTotal.value),
        canonical: "https://donoor.org/recherche/adhesions",
        keywords: t("recherche.seo.adhesions.keywords"),
      },
      associations: {
        title: t("recherche.seo.associations.title", { cat }),
        description: t("recherche.seo.associations.description"),
        canonical: "https://donoor.org/recherche/associations",
        keywords: t("recherche.seo.associations.keywords"),
      },
      "qr-codes": {
        title: t("recherche.seo.qrCodes.title"),
        description: avecNombre("qrCodes", qrCodesTotal.value),
        canonical: "https://donoor.org/qr",
        keywords: t("recherche.seo.qrCodes.keywords"),
      },
    };
    return map[activeType.value];
  });

  // OG image dynamique : route serveur Nuxt qui génère un PNG 1200×630 avec
  // le compteur réel + le label du type. CTR partage social ~30% supérieur à
  // l'OG statique (cf. SEO sprint v2, Karim 2026-06-15).
  const ogImageUrl = computed(() => {
    const counterByType: Record<string, number> = {
      tout: campaignsTotal.value + eventsTotal.value + adhesionsTotal.value,
      cagnottes: campaignsTotal.value,
      evenements: eventsTotal.value,
      adhesions: adhesionsTotal.value,
      associations: organizationsTotal.value,
      "qr-codes": qrCodesTotal.value,
    };
    const count = counterByType[activeType.value] ?? 0;
    return `${shareBase}/api/og/recherche?type=${activeType.value}&count=${count}`;
  });

  // ─── Facettes : noindex, follow ───────────────────────────────────────
  //
  // Une URL portant un paramètre (?category=, ?q=, ?ville=, prix, dates) est un
  // ÉTAT DE L'OUTIL de recherche, pas une page d'atterrissage. Elle affiche le
  // même inventaire, filtré — du contenu quasi dupliqué de la page de base.
  //
  // AVANT : ces URLs étaient `index, follow` avec un canonical pointant vers la
  // page de base. Google IGNORE ce canonical — vérifié dans Search Console le
  // 2026-08-06 : /cagnottes?category=solidarite = 11 impressions,
  // ?category=culturelle = 6, ?category=education = 3 impressions et 1 clic.
  // Ces pages sont donc bel et bien dans l'index, à consommer du budget de
  // crawl et à diluer la page canonique.
  //
  // `follow` est conservé : le jus de lien continue de circuler vers les fiches
  // listées. Le canonical reste posé (ceinture et bretelles).
  //
  // RÈGLE DE PROMOTION (écrite maintenant pour ne pas la re-débattre) : une
  // facette mérite sa propre URL de chemin indexable — /cagnottes/solidarite —
  // quand elle contient au moins 5 items indexables ET du contenu éditorial
  // propre. Aujourd'hui, avec 20 cagnottes réparties sur ~8 catégories et 103
  // des 125 associations en catégorie « autre », aucune ne l'atteint.
  const route = useRoute();
  const isFacetedView = computed(() => Object.keys(route.query).length > 0);

  // ─── Pagination : canonical AUTO-RÉFÉRENT, jamais vers la page 1 ───────
  //
  // Piège classique : faire pointer le canonical des pages 2, 3, 4… vers la
  // page 1. Google comprend alors « ces pages sont des doublons » et cesse de
  // les explorer — donc plus rien au-delà des 12 premiers items n'est atteint.
  // Chaque page de pagination porte SON PROPRE canonical, et rel=prev/next
  // exprime la séquence.
  const pageNum = computed(() => Math.max(1, currentPage?.value ?? 1));
  const lastPage = computed(() => Math.max(1, totalPages?.value ?? 1));
  const canonicalUrl = computed(
    () => `https://donoor.org${listingPagePath(activeType.value, pageNum.value)}`,
  );
  const prevHref = computed(() =>
    pageNum.value > 1
      ? `https://donoor.org${listingPagePath(activeType.value, pageNum.value - 1)}`
      : null,
  );
  const nextHref = computed(() =>
    pageNum.value < lastPage.value
      ? `https://donoor.org${listingPagePath(activeType.value, pageNum.value + 1)}`
      : null,
  );

  // L'OUTIL de recherche (`tout`) n'est jamais indexé — c'est un état d'écran,
  // pas une page d'atterrissage. Idem pour toute vue filtrée. Parité avec
  // HelloAsso, qui interdit `/e/recherche` dans son robots.txt tout en laissant
  // son annuaire crawlable.
  const isSearchTool = computed(() => activeType.value === "tout");
  const pageNoindex = computed(() => isSearchTool.value || isFacetedView.value);

  // robots cohérent avec l'indexabilité globale (staging = noindex) + noindex
  // sur l'outil de recherche et sur toute vue filtrée.
  const robotsContent = useRobotsContent(pageNoindex);

  // Suffixe « — Page N » à partir de la page 2 : deux pages du même rayon ne
  // doivent jamais porter le même <title>, sinon Google les traite en doublons.
  const pagedTitle = computed(() =>
    pageNum.value > 1
      ? t("recherche.seo.page", { titre: seoConfig.value.title, n: pageNum.value })
      : seoConfig.value.title,
  );

  useSeoMeta({
    title: () => pagedTitle.value,
    description: () => seoConfig.value.description,
    ogTitle: () => pagedTitle.value,
    ogDescription: () => seoConfig.value.description,
    ogType: "website",
    ogUrl: () => canonicalUrl.value,
    ogImage: () => ogImageUrl.value,
    ogImageWidth: 1200,
    ogImageHeight: 630,
    twitterCard: "summary_large_image",
    twitterImage: () => ogImageUrl.value,
    robots: () => robotsContent.value,
  });

  useHead({
    // `keywords` n'est plus dans le type de useSeoMeta (Nuxt 4.5) : même balise
    // <meta name="keywords">, posée ici.
    meta: computed(() => [
      { name: "keywords", content: seoConfig.value.keywords, key: "keywords" },
    ]),
    link: computed(() => [
      // Auto-référent, y compris sur les pages 2+ (cf. commentaire plus haut).
      { rel: "canonical" as const, key: "canonical", href: canonicalUrl.value },
      ...(prevHref.value
        ? [{ rel: "prev" as const, key: "prev", href: prevHref.value }]
        : []),
      ...(nextHref.value
        ? [{ rel: "next" as const, key: "next", href: nextHref.value }]
        : []),
    ]),
    script: computed(() => [
      // Breadcrumb schema
      {
        type: "application/ld+json",
        innerHTML: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Donoor", item: "https://donoor.org" },
            ...(activeType.value === "tout"
              ? [{ "@type": "ListItem", position: 2, name: "Recherche" }]
              : [
                  { "@type": "ListItem", position: 2, name: "Recherche", item: "https://donoor.org/recherche" },
                  { "@type": "ListItem", position: 3, name: tabs.find((t) => t.key === activeType.value)?.label || "" },
                ]),
          ],
        }),
      },
      // CollectionPage schema
      {
        type: "application/ld+json",
        innerHTML: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: seoConfig.value.title,
          description: seoConfig.value.description,
          url: seoConfig.value.canonical,
          provider: {
            "@type": "Organization",
            name: "Donoor",
            url: "https://donoor.org",
            logo: "https://donoor.org/icons/icon-512x512.png",
          },
          ...(activeType.value === "cagnottes" && campaigns.value.length > 0 && {
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: campaignsTotal.value,
              itemListElement: campaigns.value.slice(0, 20).map((c, i) => ({
                "@type": "ListItem",
                position: i + 1,
                item: { "@type": "DonateAction", name: c.title, url: `https://donoor.org/c/${c.slug}`, agent: { "@type": "Organization", name: c.organization_name } },
              })),
            },
          }),
          ...(activeType.value === "evenements" && events.value.length > 0 && {
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: eventsTotal.value,
              itemListElement: events.value.slice(0, 20).map((e, i) => {
                // Lieu (§10) : `Place` avec son `PostalAddress` (code postal,
                // ville, territoire, pays réel) et son point, depuis les
                // morceaux exposés — plus un simple nom.
                const lieu = lieuSchemaDepuisMorceaux(e);
                return {
                  "@type": "ListItem",
                  position: i + 1,
                  item: { "@type": "Event", name: e.title, url: `https://donoor.org/b/${e.slug}`, ...(e.start_date && { startDate: isoAvecDecalage(e.start_date, fuseauDe(e)) || e.start_date }), ...(lieu && { location: lieu }) },
                };
              }),
            },
          }),
          ...(activeType.value === "adhesions" && adhesions.value.length > 0 && {
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: adhesionsTotal.value,
              itemListElement: adhesions.value.slice(0, 20).map((p, i) => ({
                "@type": "ListItem",
                position: i + 1,
                item: { "@type": "Offer", name: p.title, url: `https://donoor.org/a/${p.slug}`, price: p.price, priceCurrency: "EUR" },
              })),
            },
          }),
          ...(activeType.value === "associations" && organizations.value.length > 0 && {
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: organizationsTotal.value,
              itemListElement: organizations.value.slice(0, 20).map((o, i) => ({
                "@type": "ListItem",
                position: i + 1,
                item: { "@type": "NGO", name: o.name, url: `https://donoor.org/associations/${o.slug}` },
              })),
            },
          }),
        }),
      },
    ]),
  });

  return { seoConfig };
}
